import React, { useEffect, useState } from 'react';
import {
    getClientTransactionUrl,
    useJunoCommonFunctions,
    validateCurrency,
} from '../../../../client/Juno/helpers';
import { useSelector } from 'react-redux';
import { validateAmount } from '../../../../client/Juno/helpers';
import { getFields, getMetadata, REQUIRED_FIELDS } from './payoutHelpers';
import PayOutModal from './PayOutModal';
import PayOutForm from './PayOutForm';
import { apiService } from '../../../../client/common/apiCallService';
import TransactionStatus from '../../TransactionStatus';

const PayOut = ({
    isModal,
    type = 'withdrawal',
    fieldList = 'some',
    onClose: parentClose,
    preSelected,
    selectedAccount,
    fromAddress,
}) => {
    let { list: typeSpecificFields, state: defaultState } = getFields(
        type,
        isModal ? 'all' : fieldList,
    );

    const { balancesCurrencies = [], clientData } = useSelector(state => state.juno);
    const balancesData = useSelector(state => state.juno.balancesCurrencies);
    const apiUrl = useSelector(state => state.config.api_url);
    const imageBaseUrl = useSelector(state => state.config.image_base_url);
    const typeSpecificMetadata = getMetadata(type);
    const [selectedAccountData, setSelectedAccountData] = useState({});

    useEffect(() => {
        if (selectedAccount) {
            setSelectedAccountData(selectedAccount);
        }
    }, [selectedAccount]);

    const {
        getBalances,
        getFeeForTransaction,
        getTransactions,
        fetchTransactionOverview,
        getSavedAccounts,
        updateAccountApi,
        saveAccountApi,
    } = useJunoCommonFunctions();
    const [formData, setFormData] = useState({
        fiatAmount: '',
        selectedCurrency: preSelected || null,
        ...defaultState,
        ...(selectedAccount || {}),
    });

    const [formFieldErrors, setFormFieldErrors] = useState({
        fiatAmount: '',
        selectedCurrency: '',
        ...defaultState,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [transactionFee, setTransactionFee] = useState(0);
    const [modals, setModals] = useState({
        openConfirmModal: false,
        openMainModal: true,
    });

    const [statusModals, setStatusModals] = useState({
        openStatusModal: false,
    });

    useEffect(() => {
        if (clientData) {
            setIsLoading(true);
            getBalances().finally(() => setIsLoading(false));
        }
    }, [clientData]);

    useEffect(() => {
        if (!formData.selectedCurrency) {
            setFormData({
                ...formData,
                selectedCurrency: balancesCurrencies[0],
            });
        }
    }, [balancesCurrencies]);

    const fetchTransactionFee = async selectedCurrency => {
        if (selectedCurrency) {
            const payloadForFee = {
                clientId: clientData?.clientId,
                transactionType: typeSpecificMetadata.transactionType,
                currencyType: 'fiat',
                fromCurrency: selectedCurrency,
            };
            const clientTransferFee = await getFeeForTransaction(payloadForFee);

            if (clientTransferFee && formData.fiatAmount) {
                setFormFieldErrors(prev => ({
                    ...prev,
                    fiatAmount: validateAmount(
                        formData.fiatAmount,
                        clientTransferFee,
                        selectedCurrency,
                        balancesData,
                    ),
                }));
            }
            setTransactionFee(clientTransferFee);
        }
    };

    useEffect(() => {
        fetchTransactionFee(formData.selectedCurrency?.currencyShortName);
    }, [formData.selectedCurrency]);

    const validateField = (name, value, selectedCurrency, typeSpecificFields) => {
        if (name === 'fiatAmount') {
            return validateAmount(
                value,
                transactionFee,
                selectedCurrency?.currencyShortName,
                balancesData,
            );
        }

        if (name === 'selectedCurrency') {
            return validateCurrency(selectedCurrency.currencyShortName, balancesData);
        }

        const dynamicField = typeSpecificFields.find(field => field.name == name);

        if (dynamicField?.hasError) {
            return dynamicField.hasError(value);
        }

        return '';
    };

    const validateAll = typeSpecificFields => {
        const newErrors = {};
        const fields = [
            'fiatAmount',
            'selectedCurrency',
            ...typeSpecificFields
                .filter(field => (field.dataKey == 'accountNickName' ? formData.save : true))
                .map(field => field.dataKey),
        ];

        fields.forEach(field => {
            const error = validateField(
                field,
                formData[field],
                formData.selectedCurrency,
                typeSpecificFields,
            );
            newErrors[field] = error;
        });

        setFormFieldErrors(newErrors);

        if (Object.values(newErrors).some(error => error)) {
            return false;
        }

        return true;
    };

    const showStatus = ({ status, amount, currency, description, buttonText }) => {
        setModals({
            openConfirmModal: false,
            openMainModal: false,
        });

        setStatusModals({
            openStatusModal: true,
            status,
            amount,
            currency,
            description,
            buttonText,
        });
    };

    const getSignedUrl = async (file, transactionImage, filecount) => {
        const payload = {
            imgcount: `file${filecount}`,
            imageType: 'clientPayout',
            transactionImage: transactionImage ? transactionImage : null,
        };
        const index = parseInt(filecount);
        try {
            return await new Promise((resolve, reject) => {
                apiService(
                    (window.location.hostname === 'localhost' ? apiUrl : '') +
                        '/restapi/get-client-presigned-url',
                    {
                        ...payload,
                    },
                    async resp => {
                        if (resp) {
                            await fetch(resp.data[0], {
                                method: 'PUT',
                                body: file,
                                headers: {
                                    'Content-Type': 'application/*',
                                    'cache-control': 'public, max-age=0',
                                },
                            })
                                .then(async res => {
                                    const imgUrl = `${imageBaseUrl}/client-payout/file${index}/${transactionImage}/documents`;
                                    resolve(imgUrl);
                                })
                                .catch(err => {
                                    console.log('err: ', err);
                                    showStatus({
                                        status: 'error',
                                        description: 'Failed to upload attachments',
                                    });
                                    reject(null);
                                });
                        }
                    },
                );
            });
        } catch (err) {
            showStatus({
                status: 'error',
                description: 'Failed to upload attachments',
            });
            return null;
        }
    };

    const selectedAccountReqMissing =
        Object.values(selectedAccountData).length > 0
            ? REQUIRED_FIELDS.filter(key => !selectedAccountData?.[key])
            : [];

    const handleCreate = async (attachments, setConfirmLoading) => {
        let uploadedAttachments;

        if (attachments?.files?.length) {
            uploadedAttachments = await Promise.all(
                attachments.files.map(async (data, index) => {
                    const transactionImage = `transactionImage${Math.floor(
                        100000 + Math.random() * 900000,
                    )}`;
                    return getSignedUrl(data, transactionImage, index);
                }),
            );

            if (uploadedAttachments.includes(null)) {
                setConfirmLoading(false);
                return;
            }
        }

        const transactionEmail = localStorage.getItem('user_name');
        const { selectedCurrency, fiatAmount, ...restFormData } = formData;

        const data = {
            ...restFormData,
            amount: formData.fiatAmount,
            currency: formData.selectedCurrency.currencyShortName,
        };

        await apiService(
            getClientTransactionUrl(apiUrl),
            {
                currencyType: 'fiat',
                type: typeSpecificMetadata.transactionType,
                transactionDetails: uploadedAttachments?.length
                    ? { ...data, attachment: uploadedAttachments }
                    : data,
                client: clientData.clientId,
                currency: formData.selectedCurrency.currencyShortName,
                transactionEmail,
                balance: {
                    balanceAmount: formData.fiatAmount,
                },
            },
            async response => {
                if (response) {
                    const commonStatusFields = {
                        status: 'pending',
                        buttonText: 'Track transaction',
                        amount: formData.fiatAmount,
                        currency: formData.selectedCurrency.currencyShortName,
                    };

                    const payload = { ...data, currency: [data.currency] };

                    const showResult = (action, success, isUpdate = false) => {
                        showStatus({
                            ...commonStatusFields,
                            description: `Has been ${typeSpecificMetadata?.messageWord} ${success ? 'and account ' + action + ' successfully' : `successfully and failed to ${action} account`}`,
                        });
                        if (success) getSavedAccounts();
                    };

                    if (selectedAccountReqMissing.length > 0) {
                        updateAccountApi(
                            payload,
                            () => showResult('updated', true),
                            () => showResult('update', false),
                        );
                    } else if (formData.save) {
                        saveAccountApi(
                            payload,
                            () => showResult('saved', true),
                            () => showResult('save', false),
                            () => {}, // Optional: can log or handle error
                        );
                    } else {
                        showStatus({
                            ...commonStatusFields,
                            description: `${typeSpecificMetadata?.transactionType} pending, check your transaction history to track its progress`,
                        });
                    }

                    getBalances();
                    getTransactions();
                    fetchTransactionOverview();
                }
            },
            err => {
                console.error(err);
                showStatus({
                    status: 'error',
                    description: 'Withdrawal failed',
                });
            },
        );

        setConfirmLoading(false);
    };

    const onClose = () => {
        setFormData({
            fiatAmount: '',
            selectedCurrency: balancesCurrencies[0],
            ...defaultState,
        });

        if (parentClose) {
            parentClose();
        }
    };

    let props = {
        formData,
        typeSpecificFields,
        setFormData,
        formFieldErrors,
        setFormFieldErrors,
        isLoading,
        transactionFee,
        validateField,
        validateAll,
        handleCreate,
        modals,
        setModals,
    };
    return (
        <>
            {isModal ? (
                <PayOutModal
                    formProps={props}
                    onClose={onClose}
                    modals={modals}
                    setModals={setModals}
                    type={type}
                    selectedAccount={selectedAccount}
                    fromAddress={fromAddress}
                    setSelectedAccountData={setSelectedAccountData}
                    selectedAccountData={selectedAccountData}
                    selectedAccountReqMissing={selectedAccountReqMissing}
                />
            ) : (
                <PayOutForm
                    {...props}
                    onClose={onClose}
                    selectedAccountReqMissing={selectedAccountReqMissing}
                    setSelectedAccountData={setSelectedAccountData}
                    type={type}
                />
            )}
            {statusModals.openStatusModal ? (
                <TransactionStatus
                    {...statusModals}
                    onClick={() => {
                        setStatusModals({ openStatusModal: false });
                        onClose();
                    }}
                />
            ) : null}
        </>
    );
};

export default PayOut;
